import { option } from 'fp-ts';
import { useEffect } from 'react';
import styled from 'styled-components';
import { SelectContainer, SelectWrapper as RawSelectWrapper } from 'ui/selects';
import { HelpToggletip } from 'ui/tooltip';
import { resetQueryBuilder } from 'modules/queryBuilder/legacyQueryBuilder/actions';
import { CatalogSelector, useCatalogContext } from 'modules/catalog/catalogSelector';
import { isDefined } from 'utils/tsHelpers';
import { camelCase } from 'utils/stringHelpers';
import { ClientDataType, CLIENT_DATA_TYPES } from 'utils/dataTypes';
import { FieldSelector } from './FieldSelector';
import { ResetButton } from './ResetButton';
import { CatalogFieldWarningBanner } from './CatalogFieldWarningBanner';
import { useCatalogSelectionContext } from './contexts/CatalogSelectionContext';
import { useTranslation } from '../hooks';
import { useAngularCatalogQueryContext } from '../contexts/AngularCatalogQueryContext';
import { useCatalogPreviewContext } from './contexts/CatalogPreviewContext';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing.values.double};
`;

const CatalogSelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing.values.half};
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  font-weight: bold;
  column-gap: ${({ theme }) => theme.spacing.values.half};
`;

const StyledText = styled.span`
  color: ${({ theme }) => theme.colors.primaryColors.cyan100};
`;

const SelectorWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const Selection = styled.div`
  ${({ theme }) => theme.styles.body.body2Strong};
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing.values.half};
`;

const SelectWrapper = styled.div<{ width: number }>`
  ${SelectContainer} {
    width: ${({ theme, width }) => theme.spacing.utils.pxToRem(width)};
  }
  ${RawSelectWrapper} {
    min-width: ${({ theme, width }) => theme.spacing.utils.pxToRem(width)};
  }
`;

export const DATA_TEST = {
  selector: 'catalog-selector',
};

const catalogSelectorWidth = 300;
const catalogFieldSelectorWidth = 350;

const getDataTypeMessage = (dataType?: ClientDataType) => {
  if (dataType === camelCase(CLIENT_DATA_TYPES.USER)) return 'user profile';
  else if (dataType === camelCase(CLIENT_DATA_TYPES.CUSTOM_EVENT)) return 'custom event';
  else return 'event';
};

export const Selector = () => {
  const { t } = useTranslation();
  const { closePreview } = useCatalogPreviewContext();
  const { update } = useCatalogSelectionContext();
  const { catalog, changeFieldDetails } = useCatalogContext();
  const { catalogQueryDetails } = useAngularCatalogQueryContext();
  const searchField = catalogQueryDetails?.searchField;
  const dataType = catalogQueryDetails?.dataType;
  const catalogId = option.toUndefined(catalog)?.id;

  useEffect(() => {
    if (isDefined(catalogId)) {
      update(false); // change isMatchingONKey to false
      changeFieldDetails(option.none); // reset the catalog field
      resetQueryBuilder(); // reset the builder
      closePreview(); // close preview table if open
    }
  }, [catalogId]);

  return (
    <Wrapper>
      <CatalogFieldWarningBanner />
      <CatalogSelectWrapper data-test={DATA_TEST.selector}>
        <TextWrapper>
          <span>{t('catalogQuery.selector', { dataType: getDataTypeMessage(dataType) })}</span>
          <StyledText>{searchField}</StyledText>
        </TextWrapper>
        <SelectorWrapper>
          <Selection>
            <SelectWrapper width={catalogSelectorWidth}>
              <CatalogSelector />
            </SelectWrapper>
            <SelectWrapper width={catalogFieldSelectorWidth}>
              <FieldSelector />
            </SelectWrapper>
            <HelpToggletip
              iconProps={{ height: 24, width: 24 }}
              tooltipContent={t('catalogQuery.fieldSelectHelpTooltip', {
                dataType: getDataTypeMessage(dataType),
              })}
            />
          </Selection>
          <ResetButton />
        </SelectorWrapper>
      </CatalogSelectWrapper>
    </Wrapper>
  );
};
